import { FormikComputedProps, FormikHandlers, FormikHelpers, FormikState, useFormik } from 'formik';
import { omit } from 'lodash';
import { useEffect, useState } from 'react';
import { checkEmptyValues, emailValidation } from '../../../core/formValidation';

export interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  position: string;
  loginCredentialsNeeded: boolean;
  isCashier: boolean;
  isManager: boolean;
  userGroupId: number | null;
  coreSites: { id: number }[];
  cashierSites: { id: number }[];
}

export type Formik = FormikHandlers &
  FormikState<FormValues> &
  FormikComputedProps<FormValues> &
  FormikHelpers<FormValues>;

type FormPage = 'employeeFormPage' | 'userFormPage';

export interface Form {
  formik: Formik;
  handleResetClick: () => void;
  handleSubmitClick: () => void;
  handleTogglePage: () => void;
  formPage: FormPage;
}

export interface FormConfigs {
  isLoading: boolean;
  onSubmit: (values: FormValues) => void;
}

interface UserFields {
  userGroupId: number;
  coreSites: { id: number }[];
}

export const userFields: (keyof UserFields)[] = ['userGroupId', 'coreSites'];

const useForm = ({ onSubmit, isLoading }: FormConfigs): Form => {
  const [formPage, setFormPage] = useState<FormPage>('employeeFormPage');

  const formik = useFormik<FormValues>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      position: '',
      loginCredentialsNeeded: false,
      isCashier: false,
      isManager: false,
      userGroupId: null,
      coreSites: [],
      cashierSites: [],
    },
    validate: (values: FormValues) => {
      let valuesToValidate: any = { ...values };
      let errors: any;

      const { cashierSites, ...valuesWithoutCashierSites } = valuesToValidate;
      valuesToValidate = valuesWithoutCashierSites;

      if (!values.loginCredentialsNeeded) {
        valuesToValidate = omit(valuesToValidate, userFields);
        errors = checkEmptyValues(valuesToValidate);
      } else {
        errors = checkEmptyValues(valuesToValidate);
      }

      // Add email format validation (always validate format when email has a value)
      if (values.email && values.email.trim()) {
        if (!emailValidation(values.email)) {
          // Set format error if email is invalid (this will make formik.isValid false)
          errors.email = 'Invalid email format';
        }
        // If email format is valid, errors.email will remain undefined (no error)
      }

      return errors;
    },
    onSubmit,
    isInitialValid: false,
    validateOnChange: true,
  });

  useEffect(() => {
    if (!isLoading && formik.isSubmitting) {
      formik.setSubmitting(false);
    }
  }, [isLoading, formik]);

  const handleResetClick = () => {
    formik.resetForm();
  };

  const handleSubmitClick = () => {
    if (!formik.isSubmitting) {
      const values = formik.values;
      if (values.email && values.email.trim() && !emailValidation(values.email)) {
        formik.setFieldTouched('email', true, false);
        formik.setFieldError('email', 'Invalid email format');
        return;
      }

      formik.submitForm();
    }
  };

  const handleTogglePage = () => {
    if (formPage === 'employeeFormPage') {
      setFormPage('userFormPage');
    } else {
      setFormPage('employeeFormPage');
    }
  };

  return {
    formik,
    handleResetClick,
    handleSubmitClick,
    handleTogglePage,
    formPage,
  };
};

export default useForm;
